// src/admin/components/AdminLayout.jsx
import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { clearAdminSession, getAdminMe } from "../adminAuth";

const BRAND = {
  pink: "#FF2D83",
};

const ADMIN_FONT_STACK =
  '"Poppins", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial';

const nav = [
  { label: "Operations", to: "/admin/ops" },
  { label: "Directory", to: "/admin/directory" },
  { label: "Compliance", to: "/admin/compliance" },
  { label: "Tracker", to: "/admin/tracker" },
  { label: "Transactions", to: "/admin/transactions" },
  { label: "Finance", to: "/admin/finance" },
  { label: "Pricing", to: "/admin/pricing" },
  { label: "Marketing", to: "/admin/marketing" },
  { label: "Requirements", to: "/admin/requirements" },
  { label: "Support", to: "/admin/support" },
  { label: "Logs", to: "/admin/logs" },
  { label: "Security", to: "/admin/security" },
  { label: "Audit Logs", to: "/admin/audit" },
];

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AdminNav({ onNavigate }) {
  return (
    <nav className="space-y-1">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              "flex items-center justify-between px-4 py-3 text-sm font-semibold transition border",
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-800 border-transparent hover:bg-slate-50 hover:border-slate-200",
            ].join(" ")
          }
          style={{ borderRadius: 10 }}
        >
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const me = getAdminMe();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = () => {
    clearAdminSession();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      style={{ fontFamily: ADMIN_FONT_STACK }}
    >
      <div className="flex min-h-screen w-full">
        {/* Desktop sidebar */}
        <aside className="hidden w-[280px] shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col">
          <div className="border-b border-slate-200 px-6 py-5">
            <img
              src="/Wayroot (1600 x 500 px).svg"
              alt="Wayroot"
              className="h-11 w-auto select-none"
              draggable={false}
            />
          </div>

          <div className="border-b border-slate-200 px-6 py-4">
            <div className="truncate text-sm font-bold text-slate-900">
              {me?.name || "Admin"}
            </div>
            <div className="mt-1 truncate text-xs text-slate-500">
              {me?.email || "—"}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span
                className="h-2 w-2"
                style={{ background: BRAND.pink, borderRadius: 2 }}
              />
              <span className="text-[11px] font-semibold text-slate-500">
                {me?.role || "ADMIN"}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            <AdminNav />
          </div>

          <div className="border-t border-slate-200 px-6 py-5">
            <button
              onClick={logout}
              className="w-full border border-slate-200 bg-white px-4 py-3 text-sm font-extrabold text-slate-900 hover:bg-slate-50"
              style={{ borderRadius: 10 }}
            >
              Logout
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="min-w-0 flex-1">
          {/* Mobile top bar only */}
          <div className="sticky top-0 z-30 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  className="inline-flex h-10 w-10 items-center justify-center border border-slate-200 bg-white text-slate-900"
                  style={{ borderRadius: 10 }}
                  aria-label="Open admin navigation"
                >
                  <MenuIcon />
                </button>

                <img
                  src="/Wayroot (1600 x 500 px).svg"
                  alt="Wayroot"
                  className="h-9 w-auto select-none"
                  draggable={false}
                />
              </div>

              <div className="text-right">
                <div className="max-w-[140px] truncate text-xs font-semibold text-slate-900">
                  {me?.name || "Admin"}
                </div>
                <div className="max-w-[140px] truncate text-[11px] text-slate-500">
                  {me?.role || "ADMIN"}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile drawer */}
          {mobileOpen ? (
            <div
              className="fixed inset-0 z-40 lg:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Admin navigation"
            >
              <button
                type="button"
                className="absolute inset-0 bg-slate-900/35"
                onClick={() => setMobileOpen(false)}
                aria-label="Close admin navigation overlay"
              />
              <div className="absolute left-0 top-0 flex h-full w-[92vw] max-w-[340px] flex-col border-r border-slate-200 bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
                  <img
                    src="/Wayroot (1600 x 500 px).svg"
                    alt="Wayroot"
                    className="h-10 w-auto"
                    draggable={false}
                  />
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="inline-flex h-10 w-10 items-center justify-center border border-slate-200 bg-white text-slate-900"
                    style={{ borderRadius: 10 }}
                    aria-label="Close admin navigation"
                  >
                    <CloseIcon />
                  </button>
                </div>

                <div className="border-b border-slate-200 px-4 py-4">
                  <div className="text-sm font-bold text-slate-900">
                    {me?.name || "Admin"}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {me?.email || "—"}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span
                      className="h-2 w-2"
                      style={{ background: BRAND.pink, borderRadius: 2 }}
                    />
                    <span className="text-[11px] font-semibold text-slate-500">
                      {me?.role || "ADMIN"}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-3 pb-24">
                  <AdminNav onNavigate={() => setMobileOpen(false)} />
                </div>

                <div className="border-t border-slate-200 bg-white px-4 py-4">
                  <button
                    onClick={logout}
                    className="w-full border border-slate-200 bg-white px-4 py-3 text-sm font-extrabold text-slate-900 hover:bg-slate-50"
                    style={{ borderRadius: 10 }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <main className="min-w-0 px-4 py-4 sm:px-6 sm:py-6 lg:px-6 lg:py-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
